import { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";  // CardHeader, CardTitleを削除
import { Badge } from "./ui/badge";
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Clock,
  Users,
  Download,
  Filter,
  BarChart3,
  FileSpreadsheet
} from "lucide-react";

export default function AnalyticsReports() {
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [activeTab, setActiveTab] = useState("overview"); // overview, cost, usage, efficiency

  // サンプルデータ
  const monthlyCostData = [
    { month: '1月', 材料費: 2400000, 人件費: 1800000, 機器費: 800000, 総額: 5000000 },
    { month: '2月', 材料費: 2200000, 人件費: 1900000, 機器費: 750000, 総額: 4850000 },
    { month: '3月', 材料費: 2600000, 人件費: 2000000, 機器費: 900000, 総額: 5500000 },
    { month: '4月', 材料費: 2300000, 人件費: 1850000, 機器費: 820000, 総額: 4970000 },
    { month: '5月', 材料費: 2500000, 人件費: 1950000, 機器費: 880000, 総額: 5330000 },
    { month: '6月', 材料費: 2400000, 人件費: 2100000, 機器費: 850000, 総額: 5350000 }
  ];

  const procedureUsageData = [
    { procedure: '幽門側胃切除', 件数: 45, 平均コスト: 185000, 総コスト: 8325000 },
    { procedure: '腹腔鏡下胆嚢摘出', 件数: 68, 平均コスト: 95000, 総コスト: 6460000 },
    { procedure: '虫垂切除術', 件数: 32, 平均コスト: 75000, 総コスト: 2400000 },
    { procedure: '人工膝関節置換', 件数: 28, 平均コスト: 320000, 総コスト: 8960000 },
    { procedure: '腹腔鏡下結腸切除', 件数: 22, 平均コスト: 210000, 総コスト: 4620000 }
  ];

  const departmentCostData = [
    { name: '消化器外科', value: 12500000, color: '#8884d8' },
    { name: '整形外科', value: 9800000, color: '#82ca9d' },
    { name: '心臓血管外科', value: 8200000, color: '#ffc658' },
    { name: '泌尿器科', value: 5400000, color: '#ff7300' },
    { name: '産婦人科', value: 4100000, color: '#00ff88' }
  ];

  const efficiencyData = [
    { month: '1月', 準備時間: 28, 手術時間: 145, 清掃時間: 22 },
    { month: '2月', 準備時間: 25, 手術時間: 142, 清掃時間: 20 },
    { month: '3月', 準備時間: 23, 手術時間: 138, 清掃時間: 18 },
    { month: '4月', 準備時間: 22, 手術時間: 135, 清掃時間: 17 },
    { month: '5月', 準備時間: 20, 手術時間: 132, 清掃時間: 16 },
    { month: '6月', 準備時間: 19, 手術時間: 130, 清掃時間: 15 }
  ];

  const materialUsageData = [
    { name: 'マイクロバイポーラ鉗子', 使用回数: 156, 単価: 1250, 総額: 195000, 在庫: 15 },
    { name: '縫合糸(3-0)', 使用回数: 284, 単価: 280, 総額: 79520, 在庫: 120 },
    { name: 'トロッカー(12mm)', 使用回数: 95, 単価: 850, 総額: 80750, 在庫: 25 },
    { name: 'ステープラー', 使用回数: 67, 単価: 2800, 総額: 187600, 在庫: 8 },
    { name: 'チタンプレート', 使用回数: 28, 単価: 45000, 総額: 1260000, 在庫: 3 }
  ];

  const kpiData = {
    monthlyRevenue: 45800000,
    monthlyRevenueChange: 5.2,
    monthlyCost: 32100000,
    monthlyCostChange: -2.1,
    operationCount: 287,
    operationCountChange: 8.5,
    averagePreparationTime: 19,
    preparationTimeChange: -32.1,
    averageEfficiency: 94.2,
    efficiencyChange: 12.3,
    stockTurnover: 8.4,
    stockTurnoverChange: 3.2
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* KPI カード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">月間収益</p>
                <p className="text-2xl font-bold">¥{(kpiData.monthlyRevenue / 1000000).toFixed(1)}M</p>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">+{kpiData.monthlyRevenueChange}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">月間コスト</p>
                <p className="text-2xl font-bold">¥{(kpiData.monthlyCost / 1000000).toFixed(1)}M</p>
              </div>
              <div className="flex items-center gap-1">
                <TrendingDown className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">{kpiData.monthlyCostChange}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">手術件数</p>
                <p className="text-2xl font-bold">{kpiData.operationCount}</p>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">+{kpiData.operationCountChange}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">平均準備時間</p>
                <p className="text-2xl font-bold">{kpiData.averagePreparationTime}分</p>
              </div>
              <div className="flex items-center gap-1">
                <TrendingDown className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">{kpiData.preparationTimeChange}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">効率性指標</p>
                <p className="text-2xl font-bold">{kpiData.averageEfficiency}%</p>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">+{kpiData.efficiencyChange}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">在庫回転率</p>
                <p className="text-2xl font-bold">{kpiData.stockTurnover}</p>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">+{kpiData.stockTurnoverChange}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* チャート */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">月間コスト推移</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyCostData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `¥${(value / 1000000).toFixed(1)}M`} />
                <Legend />
                <Bar dataKey="材料費" stackId="a" fill="#8884d8" />
                <Bar dataKey="人件費" stackId="a" fill="#82ca9d" />
                <Bar dataKey="機器費" stackId="a" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">診療科別コスト割合</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={departmentCostData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {departmentCostData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `¥${(value / 1000000).toFixed(1)}M`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderCostTab = () => (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">術式別コスト分析</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">術式名</th>
                  <th className="text-right p-2">実施件数</th>
                  <th className="text-right p-2">平均コスト</th>
                  <th className="text-right p-2">総コスト</th>
                  <th className="text-right p-2">コスト/件の変化</th>
                </tr>
              </thead>
              <tbody>
                {procedureUsageData.map((item, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{item.procedure}</td>
                    <td className="text-right p-2">{item.件数}件</td>
                    <td className="text-right p-2">¥{item.平均コスト.toLocaleString()}</td>
                    <td className="text-right p-2">¥{item.総コスト.toLocaleString()}</td>
                    <td className="text-right p-2">
                      <span className={`text-sm ${Math.random() > 0.5 ? 'text-green-600' : 'text-red-600'}`}>
                        {Math.random() > 0.5 ? '-' : '+'}
                        {(Math.random() * 10).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">材料使用コスト詳細</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">材料名</th>
                  <th className="text-right p-2">使用回数</th>
                  <th className="text-right p-2">単価</th>
                  <th className="text-right p-2">総使用額</th>
                  <th className="text-right p-2">現在在庫</th>
                  <th className="text-center p-2">状態</th>
                </tr>
              </thead>
              <tbody>
                {materialUsageData.map((item, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{item.name}</td>
                    <td className="text-right p-2">{item.使用回数}</td>
                    <td className="text-right p-2">¥{item.単価.toLocaleString()}</td>
                    <td className="text-right p-2">¥{item.総額.toLocaleString()}</td>
                    <td className="text-right p-2">{item.在庫}</td>
                    <td className="text-center p-2">
                      <Badge variant={item.在庫 > 10 ? "default" : item.在庫 > 5 ? "secondary" : "destructive"}>
                        {item.在庫 > 10 ? "十分" : item.在庫 > 5 ? "要注意" : "不足"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderUsageTab = () => (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">術式実施状況</h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={procedureUsageData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="procedure" type="category" width={150} />
              <Tooltip />
              <Bar dataKey="件数" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">よく使用される器械・材料 TOP10</h2>
          <div className="space-y-3">
            {materialUsageData.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center gap-3">
                  <div className="text-xl font-bold text-gray-400">#{index + 1}</div>
                  <div>
                    <div className="font-medium">{item.name}</div>
                    <div className="text-sm text-gray-600">使用回数: {item.使用回数}回</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium">¥{item.総額.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">単価: ¥{item.単価.toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderEfficiencyTab = () => (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">手術室効率性の推移</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={efficiencyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="準備時間" stroke="#8884d8" strokeWidth={2} />
              <Line type="monotone" dataKey="手術時間" stroke="#82ca9d" strokeWidth={2} />
              <Line type="monotone" dataKey="清掃時間" stroke="#ffc658" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4">準備時間改善</h2>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">-32%</div>
              <div className="text-sm text-gray-600">6ヶ月での改善率</div>
              <div className="mt-2 text-sm">
                28分 → 19分
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4">手術時間効率</h2>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">-10%</div>
              <div className="text-sm text-gray-600">平均手術時間短縮</div>
              <div className="mt-2 text-sm">
                145分 → 130分
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4">清掃時間短縮</h2>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">-32%</div>
              <div className="text-sm text-gray-600">清掃時間の効率化</div>
              <div className="mt-2 text-sm">
                22分 → 15分
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">ScrubEdge導入効果分析</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">時間効率の改善</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">手順確認時間</span>
                  <span className="text-sm font-medium text-green-600">-45%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">材料準備時間</span>
                  <span className="text-sm font-medium text-green-600">-38%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">器械確認時間</span>
                  <span className="text-sm font-medium text-green-600">-52%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">エラー対応時間</span>
                  <span className="text-sm font-medium text-green-600">-68%</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">コスト削減効果</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">材料ロス</span>
                  <span className="text-sm font-medium text-green-600">¥320,000削減</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">準備やり直し</span>
                  <span className="text-sm font-medium text-green-600">¥180,000削減</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">残業代</span>
                  <span className="text-sm font-medium text-green-600">¥450,000削減</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">教育コスト</span>
                  <span className="text-sm font-medium text-green-600">¥200,000削減</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">分析・レポート</h1>
        <div className="flex gap-2">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="week">直近1週間</option>
            <option value="month">直近1ヶ月</option>
            <option value="quarter">直近3ヶ月</option>
            <option value="year">直近1年</option>
          </select>
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="all">全診療科</option>
            <option value="gastro">消化器外科</option>
            <option value="orthop">整形外科</option>
            <option value="cardiac">心臓血管外科</option>
            <option value="urology">泌尿器科</option>
          </select>
          <Button>
            <Download className="w-4 h-4 mr-2" />
            レポート出力
          </Button>
        </div>
      </div>

      {/* タブナビゲーション */}
      <div className="flex gap-2 border-b">
        <Button 
          variant={activeTab === "overview" ? "default" : "ghost"}
          onClick={() => setActiveTab("overview")}
          className="border-b-2 border-transparent data-[state=active]:border-blue-500"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          概要
        </Button>
        <Button 
          variant={activeTab === "cost" ? "default" : "ghost"}
          onClick={() => setActiveTab("cost")}
        >
          <DollarSign className="w-4 h-4 mr-2" />
          コスト分析
        </Button>
        <Button 
          variant={activeTab === "usage" ? "default" : "ghost"}
          onClick={() => setActiveTab("usage")}
        >
          <Package className="w-4 h-4 mr-2" />
          使用状況
        </Button>
        <Button 
          variant={activeTab === "efficiency" ? "default" : "ghost"}
          onClick={() => setActiveTab("efficiency")}
        >
          <Clock className="w-4 h-4 mr-2" />
          効率性
        </Button>
      </div>

      {/* タブコンテンツ */}
      {activeTab === "overview" && renderOverviewTab()}
      {activeTab === "cost" && renderCostTab()}
      {activeTab === "usage" && renderUsageTab()}
      {activeTab === "efficiency" && renderEfficiencyTab()}
    </div>
  );
}